import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder'; // Import Placeholder
import {
  EditorContent,
  JSONContent,
  ReactRenderer,
  useEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { CircleHelp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import tippy, {
  GetReferenceClientRect,
  Instance as TippyInstance,
} from 'tippy.js';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useGetStaffUsersQuery } from '@/services/api/userService';

import { TaskUpdateMentionList } from './TaskUpdateMentionList';

interface TaskUpdateEditorProps {
  initialContent?: string;
  autoFocus?: boolean;
  onSave: (content: string, alertMentioned: boolean) => void | Promise<void>;
  onCancel: () => void;
  className?: string;
  placeholder?: string; // Add placeholder prop
}

export const TaskUpdateEditor = ({
  initialContent = '',
  autoFocus = false,
  onSave,
  onCancel,
  className,
  placeholder = 'Write an update...', // Default placeholder
}: TaskUpdateEditorProps) => {
  const [alertMentioned, setAlertMentioned] = useState(false);
  const { data: staffUsers = [] } = useGetStaffUsersQuery();
  const staffUsersRef = useRef(staffUsers);

  useEffect(() => {
    staffUsersRef.current = staffUsers;
  }, [staffUsers]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Mention.configure({
        HTMLAttributes: {
          class:
            'mention inline-flex items-center rounded-md bg-blue-50 px-1 py-0.5 pb-1 leading-none tracking-none text-blue-600 font-medium',
        },
        renderLabel: ({ node }) => {
          // Render as @username
          return `@${node.attrs.label}`;
        },
        suggestion: {
          char: '@',
          items: ({ query }) => {
            const users = staffUsersRef.current;
            const lowerQuery = query.toLowerCase();
            return users
              .filter((user) => {
                const fullName =
                  `${user.first_name} ${user.last_name}`.toLowerCase();
                const username = user.username.toLowerCase();
                const email = user.email.toLowerCase();
                return (
                  fullName.includes(lowerQuery) ||
                  username.includes(lowerQuery) ||
                  email.includes(lowerQuery)
                );
              })
              .slice(0, 10)
              .map((user) => ({
                id: user.id,
                label: user.username, // Username for display and what we want in the text
                value: `${user.first_name} ${user.last_name}`.trim(), // Full name as secondary info
              }));
          },
          render: () => {
            let component: ReactRenderer<any>;
            let popup: TippyInstance[];

            return {
              onStart: (props) => {
                component = new ReactRenderer(TaskUpdateMentionList, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy('body', {
                  getReferenceClientRect:
                    props.clientRect as GetReferenceClientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },

              onUpdate: (props) => {
                component.updateProps(props);

                if (!props.clientRect) {
                  return;
                }

                popup[0].setProps({
                  getReferenceClientRect:
                    props.clientRect as GetReferenceClientRect,
                });
              },

              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }
                return component.ref?.onKeyDown(props);
              },

              onExit: () => {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      }),
      // Configure Placeholder
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: React.useMemo(() => {
      if (!initialContent) {
        return {
          type: 'doc',
          content: [{ type: 'paragraph', content: [] }],
        };
      }

      // Normalize line breaks:
      // 1. Handle Windows style (\r\n)
      // 2. Collapse default TipTap double newlines (\n\n) to single (\n) to prevent extra gaps
      const normalizedContent = initialContent
        .replace(/\r\n/g, '\n')
        .replace(/\n\n/g, '\n');

      return {
        type: 'doc',
        content: normalizedContent.split('\n').map((line) => {
          if (!line) {
            return { type: 'paragraph' };
          }

          const parts = line.split(/(@[a-zA-Z0-9_]+(?:[.\-@][a-zA-Z0-9_]+)*)/g);
          const lineContent = parts
            .map((part) => {
              if (part.match(/^@[a-zA-Z0-9_]+(?:[.\-@][a-zA-Z0-9_]+)*$/)) {
                return {
                  type: 'mention',
                  attrs: { label: part.substring(1) },
                };
              }
              if (!part) return null;
              return { type: 'text', text: part };
            })
            .filter(Boolean) as JSONContent[];

          return {
            type: 'paragraph',
            content: lineContent,
          };
        }),
      };
    }, [initialContent]),
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none min-h-[48px] px-2.5 py-1.5 text-xs leading-snug text-slate-900 [&_p]:mb-1 [&_p:last-child]:mb-0 font-semibold',
          className,
        ),
      },
    },
    onCreate: ({ editor: editorInstance }) => {
      if (autoFocus) {
        editorInstance.commands.focus();
      }
    },
  });

  // Handle Ctrl+Enter to save
  useEffect(() => {
    if (!editor) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    };

    const element = editor.view.dom;
    element.addEventListener('keydown', handleKeyDown);
    return () => element.removeEventListener('keydown', handleKeyDown);
  }, [editor]);

  const handleSave = async () => {
    if (editor && !editor.isEmpty) {
      await onSave(editor.getText({ blockSeparator: '\n' }), alertMentioned);
      // Only clear if initialContent is empty (meaning it's a new, unsaved draft)
      if (!initialContent) {
        editor.commands.clearContent();
        setAlertMentioned(false);
      }
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col w-full bg-white rounded-lg border border-gray-200 overflow-hidden relative z-10 shadow-sm">
      <EditorContent editor={editor} className="flex-1 p-0" />
      <div className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center gap-1.5 select-none">
          <Checkbox
            id="alert-mentioned"
            checked={alertMentioned}
            onCheckedChange={(checked) => setAlertMentioned(checked === true)}
            className="h-3.5 w-3.5 rounded border-gray-300 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400 data-[state=checked]:text-black"
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="alert-mentioned"
            className="text-xs font-semibold text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
          >
            Alert mentioned
          </label>
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleHelp className="h-3 w-3 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[200px]">
              <p className="text-xs">
                Mentioned users will receive an in-app notification. Use for items that need immediate attention.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-7 px-3 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={editor.isEmpty}
            className="h-7 px-3 text-xs font-medium bg-black text-white hover:bg-gray-800 rounded-md transition-colors shadow-none"
          >
            Post
          </Button>
        </div>
      </div>
    </div>
  );
};
